import {
  type Dispatch,
  type ReactNode,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from 'react';

import type { TableColumnsType, TableProps } from 'antd-v5';
import type { SorterResult } from 'antd-v5/es/table/interface';
import { omit } from 'lodash';

import {
  type FilterState,
  type GroupDef,
  type GroupPaging,
  type GroupedSummary,
  type QueryFns,
  type SortState,
  type TableQueryAction,
  type TableQueryState,
  type TableRow,
  applyFilters,
  applySort,
  buildSections,
  controlColumns,
  extractQueryFns,
  filterSections,
  hasActiveQuery,
  initialTableQuery,
  pageOfGroup,
  paginateFlat,
  paginatePeople,
  paginateWholeGroups,
  pruneFilters,
  sameFilters,
  summarize,
  tableQueryReducer,
  toSortState,
} from './groupedTableModel';

export interface GroupRowContext<TGroup> {
  groupKey: string;
  /** Unset for a fallback group missing from `groups` */
  group?: TGroup;
  /** Rows of the group, collapsed or not */
  count: number;
  collapsed: boolean;
  /** The group started on an earlier page */
  continued: boolean;
  toggle: () => void;
}

export interface GroupingConfig<TData, TGroup extends GroupDef> {
  /** In display order, empty ones included */
  groups: readonly TGroup[];
  /** A row shows in each of its groups found in `groups` */
  getGroupKeys: (row: TData) => readonly string[];
  /** Group of the rows in none of `groups`: last, hidden when empty */
  fallbackKey: string;
  /** Its filter values are group keys: they pick groups. No sort while grouped */
  columnKey?: string;
  /** `unfiltered` (default): while no search or narrowing filter is on */
  showEmpty?: 'always' | 'unfiltered' | 'never';
  /** Content of the header row, one cell across the columns */
  renderGroupRow: (context: GroupRowContext<TGroup>) => ReactNode;
}

export interface UseGroupedTableOptions<TData, TGroup extends GroupDef> {
  dataSource: readonly TData[];
  /** Keep it stable (`useMemo`) */
  columns: TableColumnsType<TData>;
  getRowId: (row: TData) => string;
  /** Flat without it; keep it stable */
  grouping?: GroupingConfig<TData, TGroup>;
  groupPaging?: GroupPaging;
  search?: string;
  globalFilterFn?: (row: TData, query: string) => boolean;
  /** `false`: every row on one page */
  paginate?: boolean;
  defaultPageSize?: number;
  /** As antd's; `currentDataSource` is the filtered, sorted rows */
  onChange?: TableProps<TData>['onChange'];
}

type TableChange<TData> = NonNullable<TableProps<TData>['onChange']>;

export interface GroupedTableQuery<TData, TGroup> {
  state: TableQueryState;
  dispatch: Dispatch<TableQueryAction>;
  grouped: boolean;
  /** `columns` controlled by the state (antd "remote" mode) */
  columns: TableColumnsType<TData>;
  /** The page, header rows included */
  rows: TableRow<TData, TGroup>[];
  /** Searched, filtered and sorted, before grouping and paging */
  currentDataSource: TData[];
  page: number;
  pageSize: number;
  total: number;
  summary: GroupedSummary;
  onTableChange: TableChange<TableRow<TData, TGroup>>;
  onPageChange: (page: number, pageSize: number) => void;
  toggleGroup: (groupKey: string) => void;
}

const NO_GROUPS: readonly GroupDef[] = [];
const ALL_ROWS = Number.MAX_SAFE_INTEGER;

function queryRows<T>(
  rows: readonly T[],
  fns: QueryFns<T>,
  filters: FilterState,
  sort: SortState | null,
  skip?: readonly string[],
): T[] {
  const filtered = applyFilters(rows, fns.predicates, filters, { skip });
  return applySort(filtered, fns.compare, sort);
}

/** Query state and rows of a grouped antd table; paging doesn't re-sort. */
export function useGroupedTable<TData extends object, TGroup extends GroupDef>(
  options: UseGroupedTableOptions<TData, TGroup>,
): GroupedTableQuery<TData, TGroup> {
  const {
    dataSource,
    columns,
    getRowId,
    grouping,
    groupPaging = 'whole-groups',
    search = '',
    globalFilterFn,
    paginate = true,
    defaultPageSize,
    onChange,
  } = options;
  const grouped = !!grouping;
  const groups = (grouping?.groups ?? NO_GROUPS) as readonly TGroup[];
  const getGroupKeys = grouping?.getGroupKeys;
  const fallbackKey = grouping?.fallbackKey ?? '';
  const groupColumn = grouping?.columnKey;
  const showEmptyRule = grouping?.showEmpty ?? 'unfiltered';

  const [state, dispatch] = useReducer(tableQueryReducer, columns, cols =>
    initialTableQuery(cols, { pageSize: defaultPageSize }),
  );

  // Back to page 1 on a new search, grouping or paging rule
  const resetKey = `${grouped}|${groupPaging}|${search}`;
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    dispatch({ type: 'resetPage' });
  }

  const skip = useMemo(
    () => (grouped && groupColumn ? [groupColumn] : undefined),
    [grouped, groupColumn],
  );
  const queryFns = useMemo(() => {
    const fns = extractQueryFns(columns);
    return skip ? { ...fns, compare: omit(fns.compare, skip) } : fns;
  }, [columns, skip]);

  const columnDefaults = useMemo(() => initialTableQuery(columns), [columns]);
  // Column defaults can change with the data (an option only some rows need)
  if (!sameFilters(columnDefaults.defaultFilters, state.defaultFilters)) {
    dispatch({
      type: 'defaultFilters',
      defaultFilters: columnDefaults.defaultFilters,
    });
  }
  // A sort by the group column falls back to the default one
  const sortOf = (next: SortState | null) =>
    skip && next && skip.includes(next.columnKey) ? columnDefaults.sort : next;
  const sort = sortOf(state.sort);

  // Drop values whose option is gone, as antd's onChange does
  const filters = useMemo(
    () => pruneFilters(state.filters, columns),
    [state.filters, columns],
  );

  const searched = useMemo(
    () =>
      search.trim() && globalFilterFn
        ? dataSource.filter(row => globalFilterFn(row, search))
        : dataSource,
    [dataSource, search, globalFilterFn],
  );
  const sorted = useMemo(
    () => queryRows(searched, queryFns, filters, sort, skip),
    [searched, queryFns, filters, sort, skip],
  );

  const showEmpty =
    showEmptyRule === 'always' ||
    (showEmptyRule === 'unfiltered' &&
      !hasActiveQuery(
        { search, filters, defaultFilters: state.defaultFilters },
        { skip: groupColumn ? [groupColumn] : undefined },
      ));
  const selectedKeys = groupColumn ? filters[groupColumn] : undefined;
  const sections = useMemo(
    () =>
      getGroupKeys
        ? filterSections(
            buildSections(sorted, {
              groups,
              getGroupKeys,
              fallbackKey,
              getRowId,
            }),
            { selectedKeys, showEmpty, fallbackKey },
          )
        : [],
    [
      sorted,
      groups,
      getGroupKeys,
      fallbackKey,
      getRowId,
      selectedKeys,
      showEmpty,
    ],
  );
  const summary = useMemo<GroupedSummary>(
    () =>
      grouped
        ? summarize(sections, fallbackKey)
        : { groups: 0, items: sorted.length, rows: sorted.length },
    [grouped, sections, fallbackKey, sorted],
  );

  const collapsed = useMemo(() => new Set(state.collapsed), [state.collapsed]);
  const pageSize = paginate ? state.pageSize : ALL_ROWS;
  const requestedPage = state.page;
  const pageData = useMemo(() => {
    if (!grouped)
      return paginateFlat<TableRow<TData, TGroup>>(
        sorted,
        requestedPage,
        pageSize,
      );
    const paginateGroups =
      groupPaging === 'people' ? paginatePeople : paginateWholeGroups;
    return paginateGroups(sections, {
      page: requestedPage,
      pageSize,
      collapsed,
    });
  }, [
    grouped,
    groupPaging,
    sections,
    sorted,
    requestedPage,
    pageSize,
    collapsed,
  ]);

  const controlled = useMemo(
    () => controlColumns(columns, { sort, filters }, { disableSort: skip }),
    [columns, sort, filters, skip],
  );

  const onTableChange: GroupedTableQuery<TData, TGroup>['onTableChange'] = (
    pagination,
    changedFilters,
    sorter,
    extra,
  ) => {
    const nextSort = toSortState(sorter);
    const nextFilters = changedFilters as FilterState;
    dispatch({ type: 'tableChange', sort: nextSort, filters: nextFilters });
    onChange?.(pagination, changedFilters, sorter as SorterResult<TData>, {
      ...extra,
      currentDataSource: queryRows(
        searched,
        queryFns,
        pruneFilters(nextFilters, columns),
        sortOf(nextSort),
        skip,
      ),
    });
  };
  const onPageChange = (page: number, size: number) => {
    dispatch({ type: 'paginate', page, pageSize: size });
    onChange?.(
      { current: page, pageSize: size, total: pageData.total },
      filters,
      { columnKey: sort?.columnKey, order: sort?.order },
      { action: 'paginate', currentDataSource: sorted },
    );
  };
  const toggleGroup = useCallback(
    (key: string) => {
      const next = new Set(collapsed);
      if (!next.delete(key)) next.add(key);
      // With people paging the rows before it move, so follow its header
      const page =
        groupPaging === 'people'
          ? pageOfGroup(sections, key, { pageSize, collapsed: next })
          : undefined;
      dispatch({ type: 'toggleGroup', key, page });
    },
    [collapsed, groupPaging, sections, pageSize],
  );

  return {
    state,
    dispatch,
    grouped,
    columns: controlled,
    rows: pageData.rows,
    currentDataSource: sorted,
    page: pageData.page,
    pageSize: state.pageSize,
    total: pageData.total,
    summary,
    onTableChange,
    onPageChange,
    toggleGroup,
  };
}
